import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import SEOHead from '../components/SEOHead';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CollectionsListPage = () => {
  const [collections, setCollections] = useState([]);
  const [settings, setSettings] = useState({});
  const [allTags, setAllTags] = useState([]);
  const [activeTag, setActiveTag] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [collectionsRes, settingsRes] = await Promise.all([
          fetch(`${API_URL}/api/content/collections`),
          fetch(`${API_URL}/api/content/settings`).catch(() => ({ json: () => ({}) })),
        ]);
        const collectionsData = await collectionsRes.json();
        const settingsData = await settingsRes.json();

        const withCovers = collectionsData.filter(c => c.cover_image_url);
        setCollections(withCovers);
        setSettings(settingsData);

        // Extract unique tags
        const tags = new Set();
        withCovers.forEach(c => (c.tags || []).forEach(t => tags.add(t)));
        setAllTags([...tags].sort());
      } catch (error) {
        console.error('Error fetching collections:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filtered = activeTag === 'all'
    ? collections
    : collections.filter(c => (c.tags || []).includes(activeTag));

  const logoUrl = settings.photo_logo_url || settings.logo_url || '';

  return (
    <div className="min-h-screen bg-white" data-testid="collections-list-page">
      <SEOHead
        title="All Collections — Wedding Photography Portfolio"
        description="Browse all wedding photography collections by Crystal Media. Luxury editorial photography for Pakistani, Indian, South Asian, and multicultural weddings across Canada and the USA."
        url="/collections"
        siteSettings={settings}
      />

      <Header logoUrl={logoUrl} theme="light" />

      {/* Hero */}
      <section className="pt-32 pb-16 px-6 text-center">
        <p className="text-[0.6rem] tracking-[0.3em] uppercase text-black/30 mb-4">Portfolio</p>
        <h1 className="font-presti text-4xl md:text-5xl text-black/85 mb-4">All Collections</h1>
        <p className="text-sm text-black/40 max-w-md mx-auto">
          Every love story, captured with intention and artistry
        </p>
      </section>

      {/* Tag filters */}
      {allTags.length > 0 && (
        <div className="flex flex-wrap justify-center gap-2 px-6 mb-12" data-testid="tag-filters">
          <button
            onClick={() => setActiveTag('all')}
            className={`px-4 py-1.5 text-xs tracking-[0.15em] uppercase rounded-full transition-all ${
              activeTag === 'all'
                ? 'bg-black text-white'
                : 'border border-black/15 text-black/50 hover:border-black/40'
            }`}
            data-testid="tag-filter-all"
          >
            All
          </button>
          {allTags.map(tag => (
            <button
              key={tag}
              onClick={() => setActiveTag(tag)}
              className={`px-4 py-1.5 text-xs tracking-[0.15em] uppercase rounded-full transition-all ${
                activeTag === tag
                  ? 'bg-black text-white'
                  : 'border border-black/15 text-black/50 hover:border-black/40'
              }`}
              data-testid={`tag-filter-${tag}`}
            >
              {tag}
            </button>
          ))}
        </div>
      )}

      {/* Collections grid */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        {loading ? (
          <div className="text-center py-20 text-black/30">Loading collections...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-black/30">No collections found</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map((collection) => (
              <Link
                key={collection.id}
                to={`/collection/${collection.id}`}
                className="group block"
                data-testid={`collection-card-${collection.id}`}
              >
                <div className="aspect-[3/4] overflow-hidden bg-gray-100 mb-4">
                  <img
                    src={collection.cover_image_url}
                    alt={`${collection.couple} — ${collection.location}`}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    style={{
                      objectPosition: collection.cover_focal_point
                        ? `${collection.cover_focal_point.x}% ${collection.cover_focal_point.y}%`
                        : 'center'
                    }}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium tracking-wide text-black/80 group-hover:text-black transition-colors">
                      {collection.couple}
                    </h3>
                    <p className="text-xs text-black/40 mt-0.5">{collection.location}</p>
                  </div>
                  <ArrowRight size={14} className="text-black/20 group-hover:text-black/60 transition-all group-hover:translate-x-1" />
                </div>
                {(collection.tags || []).length > 0 && (
                  <div className="flex gap-1.5 mt-2">
                    {collection.tags.map((tag, i) => (
                      <span key={i} className="text-[10px] tracking-wider uppercase text-black/30">{tag}</span>
                    ))}
                  </div>
                )}
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default CollectionsListPage;
